/* eslint-disable @typescript-eslint/no-explicit-any */
import { createPopper } from '@popperjs/core';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as ArrowDown } from '../../assets/svg/ArrowDown.svg';
import { useOutsideAlerter } from '../../hooks';


interface SelectProps {
    value: any,
    options: { label?: string, value: any, color?: boolean | string, disabled?: boolean }[],
    minimized?: boolean,
    radio?: boolean,
    nobd?: boolean,
    top?: boolean,
    simple?: boolean,
    nosearch?: boolean,
    placeholder?: string,
    className?: string,
    text?: boolean,
    onChange?: (value: any, revert: (v: any) => void) => void,
    disabled?: boolean,
    error?: string,
    dataTestId?: string,
    children?: React.ReactNode,
    style?: CSSProperties,
    containerStyle?: CSSProperties,
}

function Select({ value, options, onChange, containerStyle, placeholder, simple, minimized, radio, text, className, nosearch, nobd, top, disabled, error, dataTestId, style, children }: SelectProps) {
    const refDiv = useRef<HTMLDivElement | null>(null);

    const [label, setLabel] = useState<string>();
    const [val, setVal] = useState<string>(value);
    const [isExpand, setIsExpand] = useState(false);
    const [filter, setFilter] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);

    const setLocalVal = useCallback((v: any) => {
        if (options.length > 0) {
            if (v === undefined) {
                setVal('');
                setLabel(undefined);
            } else {
                const l = options.find(o => o.value === v)?.label;
                if (l) {
                    setVal(v);
                    setLabel(l);
                } else {
                    setVal('');
                    setLabel(undefined);
                }
            }
        }
    }, [options]);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    useOutsideAlerter((minimized || simple) ? refDiv : wrapperRef, setIsExpand, (minimized || simple) ? wrapperRef : undefined);

    useEffect(() => {
        if (!isExpand) {
            setFilter('');
        } else {
            inputRef.current?.focus();
        }
    }, [isExpand]);

    useEffect(() => {
        if (refDiv.current && wrapperRef.current) {
            createPopper(wrapperRef.current, refDiv.current, {
                placement: 'bottom-start',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, minimized ? 4 : (simple ? -2 : 0)],
                        },
                    },
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: ['top-start', 'top-end'],
                        },
                    },
                ],
            });
        }
    }, [isExpand]);

    useEffect(() => {
        setFilteredOptions(options
            .filter(o => o.label) // фильтруем пустые строки
            .filter(o => !filter || o.label?.toLowerCase().includes(filter.toLowerCase())));
    }, [filter, options]);

    useEffect(() => {
        if (options.length > 0) {
            if (value === undefined) {
                setVal('');
                setLabel(undefined);
                // const option = options.at(0);
                // if (option) {
                //     setLabel(option.label);
                //     setVal(option.value);
                // }
            } else {
                const l = options.find(o => o.value === value)?.label;
                if (l) {
                    setVal(value);
                    setLabel(l);
                } else {
                    setVal('');
                    setLabel(undefined);
                }
            }
        }
    }, [options, value]);

    return (
        <div className={className || 'form__item'} style={containerStyle}>
            <div ref={wrapperRef} className={'comboInput combo-select _single' + (disabled ? ' _disabled' : '') + (simple ? ' _simple' : '') + (radio ? ' _radio' : '') + (isExpand ? ' _active' : '') + (((val !== undefined && val !== '') || isExpand) ? ' _selected' : '')
                + (minimized ? ' _min' : '') + (nobd ? ' _nobd' : '') + (top ? ' _top' : '') + (text ? ' _text' : '') + (!value && placeholder ? ' _placeholder' : '')}
                data-test-id={dataTestId}
                onClick={() => {
                    if (disabled) return;
                    setIsExpand(!isExpand);
                }}
                style={{ borderColor: error ? 'var(--c-red)' : undefined, ...style }}>
                <span className="comboInput__label">
                    {label || (!isExpand && placeholder)}
                    {!nosearch && !simple &&
                        <span className="comboInput__search" style={{ display: 'inline-block' }}>
                            <input ref={inputRef} value={filter} type="text" size={1} onChange={(e) => setFilter(e.target.value)} />
                        </span>
                    }
                </span>
                <span className="comboInput__icon">
                    <ArrowDown />
                </span>
                {isExpand && !(minimized || simple) &&
                    <div
                        className={'comboInput__list _single'}
                    >
                        {filteredOptions.map((option) => (
                            <div
                                key={option.value}
                                className={'comboInput__list-item' + (option.value === val ? ' _active' : '')}
                                onClick={() => {
                                    setVal(option.value);
                                    setLabel(option.label);
                                    onChange && onChange(option.value, (v) => setLocalVal(v));
                                }}
                                style={{ color: option.color ? (option.color === true ? option.value : option.color) : undefined}}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                }
                {isExpand && (minimized || simple) && ReactDOM.createPortal(
                    <div
                        ref={refDiv}
                        className={'comboInput__list _single' + (simple ? ' _simple' : ' _min')}
                        style={{
                            width: `${(wrapperRef.current?.clientWidth || 0) + 2}px`,
                            display: 'block',
                            position: 'absolute',
                            inset: '0px auto auto 0px',
                            margin: '0px',
                            zIndex: 40,
                        }}
                    >
                        {options.map((option) => (
                            <div
                                key={option.value}
                                className={'comboInput__list-item' + (option.value === val ? ' _active' : '')}
                                onClick={(e) => {
                                    if (option.disabled) {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        return;
                                    }
                                    setVal(option.value);
                                    setLabel(option.label);
                                    onChange && onChange(option.value, (v) => setLocalVal(v));
                                }}
                                style={{ color: option.disabled ? 'var(--c-text-grey)' : (option.color ? (option.color === true ? option.value : option.color) : undefined)}}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                    , document.body)}
            </div>
            {children}
            {error && <div className="form__input-error-message">{error}</div>}
        </div>
    );
}

export default Select;
